import React, { useState, useEffect } from 'react';
import { 
  Search, Video, Edit3, Trash2, Eye, Filter, 
  Download, Upload, Settings, BarChart3, Users, 
  Calendar, Tag, Star, PlayCircle, ExternalLink,
  TrendingUp, Clock, Globe, Shield, Zap, 
  CheckCircle, XCircle, AlertCircle, MoreVertical,
  Loader2, RefreshCw, Plus
} from 'lucide-react';

import videoServices from '../CP-Academy/VideoService';
import { useAdminAuth } from './AdminAuthContext';

// Configuration des thèmes avec métadonnées étendues
const VIDEO_THEMES = {
  entrepreneurship: { 
    name: '🚀 Entrepreneuriat', 
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-800',
    description: 'Business, startup, innovation'
  },
  technology: { 
    name: '💻 Technologie', 
    color: 'purple',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-800',
    description: 'Tech, développement, IA'
  },
  finance: { 
    name: '💰 Finance', 
    color: 'green',
    bgColor: 'bg-green-50',
    textColor: 'text-green-800',
    description: 'Finance, investissement, économie'
  },
  leadership: { 
    name: '👑 Leadership', 
    color: 'orange',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-800',
    description: 'Management, équipe, coaching'
  },
  health: { 
    name: '🌱 Santé', 
    color: 'teal',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-800',
    description: 'Santé, bien-être, lifestyle'
  },
  creativity: { 
    name: '🎨 Créativité', 
    color: 'pink',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-800',
    description: 'Art, design, innovation'
  }
};

// Composant de carte vidéo administrative
const AdminVideoCard = ({ video, onEdit, onDelete, onView, onToggleStatus }) => {
  const [showActions, setShowActions] = useState(false);
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      case 'pending': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDiscoveryMethodIcon = (method) => {
    switch (method) {
      case 'backend_ai': return <Zap className="w-4 h-4 text-purple-600" />;
      case 'manual': return <Edit3 className="w-4 h-4 text-blue-600" />;
      case 'youtube': return <PlayCircle className="w-4 h-4 text-red-600" />;
      default: return <Video className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return 'Date invalide';
    }
  };

  const theme = VIDEO_THEMES[video.theme] || VIDEO_THEMES.entrepreneurship;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all">
      {/* Header avec image ou gradient */}
      <div className={`relative h-48 ${theme.bgColor}`}>
        {video.thumbnail ? (
          <img 
            src={video.thumbnail} 
            alt={video.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Video className="w-16 h-16 text-gray-400 opacity-50" />
          </div>
        )}
        
        {/* Overlay avec statut et actions */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent">
          <div className="absolute top-4 left-4">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(video.status)}`}>
              {video.status || 'active'}
            </span>
          </div>
          
          <div className="absolute top-4 right-4">
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-white" />
              </button>
              
              {showActions && (
                <div className="absolute right-0 top-10 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-40 z-10">
                  <button
                    onClick={() => { onView(video); setShowActions(false); }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Voir
                  </button>
                  <button
                    onClick={() => { onEdit(video); setShowActions(false); }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    Modifier
                  </button>
                  <button
                    onClick={() => { onToggleStatus(video); setShowActions(false); }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                  >
                    {video.status === 'active' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                    {video.status === 'active' ? 'Désactiver' : 'Activer'}
                  </button>
                  <hr className="my-2" />
                  <button
                    onClick={() => { onDelete(video); setShowActions(false); }}
                    className="w-full text-left px-4 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Méthode de découverte */}
          <div className="absolute bottom-4 left-4">
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
              {getDiscoveryMethodIcon(video.discoveryMethod)}
              <span className="text-white text-xs font-medium">
                {video.discoveryMethod === 'backend_ai' ? 'IA' : 
                 video.discoveryMethod === 'manual' ? 'Manuel' : 
                 video.discoveryMethod === 'youtube' ? 'YouTube' : 'Auto'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Titre et thème */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-1 ${theme.bgColor} ${theme.textColor} rounded-full text-xs font-medium`}>
              {theme.name}
            </span>
            {video.aiValidated && (
              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium flex items-center gap-1">
                <Zap className="w-3 h-3" />
                IA Validé
              </span>
            )}
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
            {video.title}
          </h3>
          
          {video.description && (
            <p className="text-gray-600 text-sm line-clamp-3">
              {video.description}
            </p>
          )}
        </div>

        {/* Métadonnées */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(video.addedAt)}</span>
          </div>
          
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span className="font-medium text-gray-700">
              {video.viewCount ? video.viewCount.toLocaleString('fr-FR') : '0'} vues
            </span>
          </div>
          
          {video.domain && (
            <div className="flex items-center gap-1">
              <Globe className="w-4 h-4" />
              <span className="truncate">{video.domain}</span>
            </div>
          )}
          
          {video.addedBy && (
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              <span className="truncate">{video.addedBy}</span>
            </div>
          )}
        </div>

        {/* Scores et indicateurs */}
        {(video.relevanceScore || video.popularityScore) && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {video.relevanceScore && (
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">{Number(video.relevanceScore).toFixed(1)}/10</div>
                <div className="text-xs text-gray-500">Pertinence</div>
              </div>
            )}
            
            {video.popularityScore && (
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">{Number(video.popularityScore).toFixed(1)}/10</div>
                <div className="text-xs text-gray-500">Popularité</div>
              </div>
            )}
          </div>
        )}

        {/* Actions principales */}
        <div className="flex gap-2">
          <button
            onClick={() => onView(video)}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Ouvrir
          </button>
          
          <button
            onClick={() => onEdit(video)}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Edit3 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant de filtres avancés
const VideoFilters = ({ filters, onFiltersChange, onReset }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Filter className="w-5 h-5" />
          Filtres de recherche
        </h3>
        
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          {showAdvanced ? 'Masquer' : 'Avancé'}
        </button>
      </div>

      {/* Filtres de base */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={filters.search || ''}
              onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
              placeholder="Titre, description..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Thème</label>
          <select
            value={filters.theme || ''}
            onChange={(e) => onFiltersChange({ ...filters, theme: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tous les thèmes</option>
            {Object.entries(VIDEO_THEMES).map(([key, theme]) => (
              <option key={key} value={key}>{theme.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
          <select
            value={filters.status || ''}
            onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="draft">Brouillon</option>
            <option value="archived">Archivé</option>
            <option value="pending">En attente</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tri</label>
          <select
            value={filters.sortBy || 'addedAt'}
            onChange={(e) => onFiltersChange({ ...filters, sortBy: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="addedAt">Date d'ajout</option>
            <option value="title">Titre</option>
            <option value="viewCount">Nombre de vues</option>
            <option value="relevanceScore">Score de pertinence</option>
          </select>
        </div>
      </div>

      {/* Filtres avancés */}
      {showAdvanced && (
        <div className="border-t border-gray-200 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Méthode de découverte</label>
              <select
                value={filters.discoveryMethod || ''}
                onChange={(e) => onFiltersChange({ ...filters, discoveryMethod: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Toutes les méthodes</option>
                <option value="backend_ai">Découverte IA</option>
                <option value="manual">Ajout manuel</option>
                <option value="youtube">YouTube Direct</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ajouté par</label>
              <input
                type="text"
                value={filters.addedBy || ''}
                onChange={(e) => onFiltersChange({ ...filters, addedBy: e.target.value })}
                placeholder="Email de l'administrateur"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={onReset}
                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant principal de gestion des vidéos
const AdminVideoManager = () => {
  const { user } = useAdminAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    theme: '',
    status: '',
    sortBy: 'addedAt',
    discoveryMethod: '',
    addedBy: ''
  });
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    byTheme: {},
    byMethod: {}
  });

  // Charger toutes les vidéos
  useEffect(() => {
    loadVideos();
  }, []);

  // Calculer les statistiques
  useEffect(() => {
    calculateStats();
  }, [videos]);

  const loadVideos = async () => {
    setLoading(true);
    try {
      const allVideos = await videoServices.getAllVideos();
      setVideos(allVideos || []);
    } catch (error) {
      console.error('Erreur chargement vidéos:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const total = videos.length;
    const active = videos.filter(v => v.status === 'active').length;
    
    const byTheme = {};
    const byMethod = {};
    
    videos.forEach(video => {
      // Par thème
      byTheme[video.theme] = (byTheme[video.theme] || 0) + 1;
      
      // Par méthode de découverte
      const method = video.discoveryMethod || 'unknown';
      byMethod[method] = (byMethod[method] || 0) + 1;
    });

    setStats({ total, active, byTheme, byMethod });
  };

  // Filtrer les vidéos selon les critères
  const filteredVideos = videos.filter(video => {
    const matchesSearch = !filters.search || 
      video.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
      video.description?.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesTheme = !filters.theme || video.theme === filters.theme;
    const matchesStatus = !filters.status || video.status === filters.status;
    const matchesMethod = !filters.discoveryMethod || video.discoveryMethod === filters.discoveryMethod;
    const matchesAddedBy = !filters.addedBy || 
      video.addedBy?.toLowerCase().includes(filters.addedBy.toLowerCase());

    return matchesSearch && matchesTheme && matchesStatus && matchesMethod && matchesAddedBy;
  }).sort((a, b) => {
    const field = filters.sortBy;
    if (field === 'addedAt') {
      return new Date(b.addedAt || 0) - new Date(a.addedAt || 0);
    }
    if (field === 'title') {
      return (a.title || '').localeCompare(b.title || '');
    }
    if (field === 'viewCount') {
      return (b.viewCount || 0) - (a.viewCount || 0);
    }
    if (field === 'relevanceScore') {
      return (b.relevanceScore || 0) - (a.relevanceScore || 0);
    }
    return 0;
  });

  const handleViewVideo = (video) => {
    if (video.url) {
      window.open(video.url, '_blank');
    }
  };

  const handleEditVideo = (video) => {
    setSelectedVideo(video);
    setShowVideoModal(true);
  };

  const handleDeleteVideo = async (video) => {
    if (!window.confirm(
      `⚠️ Suppression définitive\n\n` +
      `Êtes-vous sûr de vouloir supprimer cette vidéo ?\n\n` +
      `📹 "${video.title}"\n` +
      `👀 ${video.viewCount || 0} vues\n\n` +
      `Cette action est irréversible.`
    )) {
      return;
    }

    try {
      await videoServices.deleteVideo(video.id, user?.email);
      await loadVideos();
      // Optionnel: ajouter une notification de succès
    } catch (error) {
      console.error('Erreur suppression vidéo:', error);
      alert('❌ Erreur lors de la suppression. Veuillez réessayer.');
    }
  };

  const handleToggleStatus = async (video) => {
    try {
      const newStatus = video.status === 'active' ? 'archived' : 'active';
      await videoServices.updateVideo(video.id, { status: newStatus });
      await loadVideos();
    } catch (error) {
      console.error('Erreur changement statut:', error);
      alert('Erreur lors du changement de statut');
    }
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      theme: '',
      status: '',
      sortBy: 'addedAt',
      discoveryMethod: '',
      addedBy: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                📚 Gestionnaire de Vidéos
              </h1>
              <p className="text-gray-600">
                Administration complète de votre bibliothèque de formations vidéo
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={loadVideos}
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
            </div>
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-xl">
                <Video className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString('fr-FR')}</div>
                <div className="text-sm text-gray-600">Total vidéos</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-3 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.active.toLocaleString('fr-FR')}</div>
                <div className="text-sm text-gray-600">Actives</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-3 rounded-xl">
                <Zap className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{(stats.byMethod.backend_ai || 0).toLocaleString('fr-FR')}</div>
                <div className="text-sm text-gray-600">Découverte IA</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="bg-orange-100 p-3 rounded-xl">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{filteredVideos.length.toLocaleString('fr-FR')}</div>
                <div className="text-sm text-gray-600">Filtrées</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <VideoFilters
          filters={filters}
          onFiltersChange={setFilters}
          onReset={resetFilters}
        />

        {/* Liste des vidéos */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredVideos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredVideos.map(video => (
              <AdminVideoCard
                key={video.id}
                video={video}
                onEdit={handleEditVideo}
                onDelete={handleDeleteVideo}
                onView={handleViewVideo}
                onToggleStatus={handleToggleStatus}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucune vidéo trouvée
            </h3>
            <p className="text-gray-600">
              {filters.search || filters.theme || filters.status 
                ? 'Aucune vidéo ne correspond à vos critères de recherche'
                : 'Aucune vidéo n\'a été ajoutée pour le moment'
              }
            </p>
            {(filters.search || filters.theme || filters.status) && (
              <button
                onClick={resetFilters}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        )}

        {/* Modal de détails/édition (placeholder) */}
        {showVideoModal && selectedVideo && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900">Détails de la vidéo</h3>
                  <button 
                    onClick={() => setShowVideoModal(false)}
                    className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  {selectedVideo.title}
                </h4>
                <p className="text-gray-600 mb-4">{selectedVideo.description}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <span className="text-sm text-gray-500">Thème:</span>
                    <p className="font-medium">{VIDEO_THEMES[selectedVideo.theme]?.name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Statut:</span>
                    <p className="font-medium">{selectedVideo.status}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Ajouté le:</span>
                    <p className="font-medium">{new Date(selectedVideo.addedAt).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Par:</span>
                    <p className="font-medium">{selectedVideo.addedBy}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <a
                    href={selectedVideo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-xl hover:bg-blue-700 text-center"
                  >
                    Ouvrir la vidéo
                  </a>
                  <button
                    onClick={() => setShowVideoModal(false)}
                    className="bg-gray-200 text-gray-800 px-4 py-3 rounded-xl hover:bg-gray-300"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminVideoManager;